import React, { useState } from "react";
import TypedModal from "@/components/TypedModal/TypedModal";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface FinalizeSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  waitlistCount?: number;
}

function FinalizeSessionModal({
  isOpen,
  onClose,
  onConfirm,
  waitlistCount = 0,
}: FinalizeSessionModalProps) {
  const [isWorking, setIsWorking] = useState(false);

  const handleConfirm = async () => {
    setIsWorking(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error("Failed to finalize session:", error);
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <TypedModal
      ariaHideApp={false}
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        content: {
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "8px",
          padding: "24px",
          background: "white",
          boxShadow: "0 10px 25px rgba(0,0,0,.3)",
          width: "calc(100% - 32px)",
          maxWidth: "500px",
          minBlockSize: "fit-content",
          overflowY: "auto",
        },
        overlay: {
          backgroundColor: "rgba(0,0,0,0.3)",
          zIndex: 9999,
        },
      }}
    >
      <h3 className="text-xl font-semibold text-gray-900">
        Finalize Event Session
      </h3>
      <p className="mt-3 text-sm text-gray-600">
        <strong>Warning:</strong> Once the session is finalized, changes cannot
        be reverted. Are you sure you want to proceed?
      </p>

      {waitlistCount > 0 && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-yellow-800 font-medium">Waitlist Notice</p>
              <p className="text-yellow-700 mt-1">
                There {waitlistCount === 1 ? "is" : "are"} currently{" "}
                {waitlistCount} {waitlistCount === 1 ? "person" : "people"} on
                the waitlist for this session. Finalizing will prevent any
                further waitlist processing.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-end space-x-3">
        <Button disabled={isWorking} onClick={onClose} variant="outline">
          Cancel
        </Button>
        <Button disabled={isWorking} onClick={handleConfirm}>
          {isWorking ? "Finalizing..." : "Finalize"}
        </Button>
      </div>
    </TypedModal>
  );
}

export default FinalizeSessionModal;
